import axios from "axios";
import { GenericInterfaces } from "@/interfaces";
import { SubscriptionInterfaces } from "@/interfaces/subscriptions";

const baseURL = process.env.VUE_APP_API_URL;
const PENDING_PAYMENT_KEY = "pendingPaymentCheck";

class DonationService {
  static getCaseDetails(caseId: number, referenceId: string) {
    let url = `${baseURL}/charity/case/${caseId}/get-donation-info/`;
    url += referenceId ? `?reference_id=${referenceId}` : ''

    return axios.get(url);
  }

  static getPaymentId(requestBody: any = {}, reference_id: string) {
    let url = `${baseURL}/payment/hyperpay/checkout/`;
    url += reference_id ? `?reference_id=${reference_id}` : ''

    return axios.post(url, requestBody);
  }
  static getSubscriptionPaymentId(requestBody: any = {}, apiOptions: any = {}) {
    const url = `${baseURL}/payment/hyperpay/subscription/checkout/`;

    return axios.post(url, requestBody, apiOptions);
  }
  static getSubscriptions(queryParams: GenericInterfaces.IQueryParams = {}) {
    const url = `${baseURL}/payment/hyperpay/subscriptions?order_by=${queryParams.order_by}&sort=${queryParams.sort}`;
    return axios.get<SubscriptionInterfaces.ISubscriptionsResponse>(url);
  }
  static deleteSubscription(id: string) {
    const url = `${baseURL}/payment/hyperpay/subscriptions/${id}`;

    return axios.delete(url);
  }
  static pauseSubscription(id: string) {
    const url = `${baseURL}/payment/hyperpay/subscriptions/${id}/pause/`;

    return axios.patch(url);
  }
  static activateSubscription(id: string) {
    const url = `${baseURL}/payment/hyperpay/subscriptions/${id}/activate/`;

    return axios.patch(url);
  }
  static getAmbassadorPaymentId(requestBody: any = {}, id: string) {
    const url = `${baseURL}/payment/hyperpay/checkout/?reference_id=${id}`;

    return axios.post(url, requestBody);
  }

  static getPaymentStatus(checkoutID: number, resourcePath: string) {
    const url = `${baseURL}/payment/hyperpay/result/?checkout_id=${checkoutID}&resourcePath=${resourcePath}`;

    return axios.get(url);
  }

  static async getPaymentStatusWithRetry(
    checkoutID: number,
    resourcePath: string,
    retries = 2,
    delayMs = 1000
  ): Promise<any> {
    try {
      return await DonationService.getPaymentStatus(checkoutID, resourcePath);
    } catch (error: any) {
      const isNetworkError = !error?.response || error?.code === "ERR_NETWORK";
      if (retries > 0 && isNetworkError) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return DonationService.getPaymentStatusWithRetry(
          checkoutID,
          resourcePath,
          retries - 1,
          delayMs * 2
        );
      }
      if (isNetworkError) {
        DonationService.savePendingPaymentCheck(checkoutID, resourcePath);
        return {
          data: {
            status: 202,
            result: {
              isNetworkError: true,
            },
          },
        };
      }
      throw error;
    }
  }

  static savePendingPaymentCheck(checkoutID: number, resourcePath: string): void {
    localStorage.setItem(
      PENDING_PAYMENT_KEY,
      JSON.stringify({ checkoutID, resourcePath })
    );
  }

  static getPendingPaymentCheck(): { checkoutID: number; resourcePath: string } | null {
    const stored = localStorage.getItem(PENDING_PAYMENT_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  static clearPendingPaymentCheck(): void {
    localStorage.removeItem(PENDING_PAYMENT_KEY);
  }
  static getPaymentInfo(checkoutID: number, wallet_id?: number) {
    let url = `${baseURL}/payment/hyperpay/info?checkout_id=${checkoutID}`;
    if (wallet_id !== undefined) {
        url += `&wallet_id=${wallet_id}`;
    }
  
    return axios.get(url);
}
  static donate(requestBody: any = {}, isQuickDonation = false) {
    let url = `${baseURL}/wallet/donation/`;
    url += isQuickDonation ? '?quick-donation=true' : '';

    return axios.post(url, requestBody);
  }

  static payZakat(payload: any = {}) {
    let url = `${baseURL}/wallet/pay-zakat/`;
    return axios.post(url, payload);
  }

  static checkCardType(cardNumber: any) {

    let cardType = '';

    let visaRegEx = /^(?:4[0-9]{12}(?:[0-9]{3})?)$/;
    let mastercardRegEx = /^(?:5[1-5][0-9]{14})$/;
    let madaRegEx = /(4(0(0861|1757|7(197|395)|9201)|1(0685|7633|9593)|2(281(7|8|9)|8(331|67(1|2|3)))|3(1361|2328|4107|9954)|4(0(533|647|795)|5564|6(393|404|672))|5(5(036|708)|7865|8456)|6(2220|854(0|1|2|3))|8(301(0|1|2)|4783|609(4|5|6)|931(7|8|9))|93428)|5(0(4300|8160)|13213|2(1076|4(130|514)|9(415|741))|3(0906|1095|2013|5(825|989)|6023|7767|9931)|4(3(085|357)|9760)|5(4180|7606|8848)|8(5265|8(8(4(5|6|7|8|9)|5(0|1))|98(2|3))|9(005|206)))|6(0(4906|5141)|36120)|9682(0(1|2|3|4|5|6|7|8|9)|1(0|1)))\d{10}$/;
    if (visaRegEx.test(cardNumber)) {
      cardType = 'visa'
    }
    else if (mastercardRegEx.test(cardNumber)) {
      cardType = 'master'
    }
    else if (madaRegEx.test(cardNumber)) {
      cardType = 'mada'
    }
    else {
      cardType = 'applepay'
    }

    return cardType;
  }
}

export default DonationService;
